import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

interface VideoBannerProps {
  sources: { src: string; type: string }[];
  poster: string;
  className?: string;
}

const VideoBanner = ({ sources, poster, className = "" }: VideoBannerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {
            /* autoplay blocked; poster is shown */
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !muted;
    setMuted(next);
    video.muted = next;
    if (!next && video.paused) {
      video.play().catch(() => {
        setMuted(true);
        video.muted = true;
      });
    }
  };

  return (
    <div
      className={`mx-auto max-w-6xl px-6 pt-8 md:pt-10 ${className}`.trim()}
    >
      <div className="relative overflow-hidden rounded-2xl bg-muted shadow-lg">
        {reducedMotion ? (
          <img
            src={poster}
            alt=""
            aria-hidden="true"
            className="aspect-video w-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              className="pointer-events-none aspect-video w-full object-cover"
              poster={poster}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              disablePictureInPicture
              disableRemotePlayback
              controlsList="nodownload nofullscreen noremoteplayback"
              onContextMenu={(e) => e.preventDefault()}
            >
              {sources.map((source) => (
                <source key={source.src} src={source.src} type={source.type} />
              ))}
            </video>
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Skru på lyd" : "Skru av lyd"}
              aria-pressed={!muted}
              className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-foreground/60 text-background shadow-lg backdrop-blur-sm transition-all hover:bg-foreground/80 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {muted ? (
                <VolumeX className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Volume2 className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoBanner;
